interface Feature<T> {
  type: 'Feature';
  properties: object;
  geometry: T;
}

interface GeometryArrays {
  points: Feature<GeoJSON.Point>[];
  polylines: Feature<GeoJSON.LineString | GeoJSON.MultiLineString>[];
  multipoints: Feature<GeoJSON.MultiPoint>[];
  polygons: Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>[];
}

export const typeNames = {
  points: 'points',
  polylines: 'tracks',
  multipoints: 'points',
  polygons: 'areas',
};

export const symbolTypes = {
  points: 'simple-marker',
  polylines: 'simple-line',
  multipoints: 'simple-marker',
  polygons: 'simple-fill',
};

/**
 * Split the GeoJSON Features into the types for ArcGIS JS
 * @param geojson GeoJSON to split
 * @returns Split features by type
 */
export function splitGeometryTypes(geojson: GeoJSON.GeoJSON) {
  const points: Feature<GeoJSON.Point>[] = [];
  const polylines: Feature<GeoJSON.LineString | GeoJSON.MultiLineString>[] = [];
  const multipoints: Feature<GeoJSON.MultiPoint>[] = [];
  const polygons: Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>[] = [];

  iterateGeojson(geojson, {
    points,
    polylines,
    multipoints,
    polygons,
  });

  return {
    points: points.length ? points : null,
    polylines: polylines.length ? polylines : null,
    multipoints: multipoints.length ? multipoints : null,
    polygons: polygons.length ? polygons : null,
  };
}

function iterateGeojson(
  geojson: GeoJSON.GeoJSON,
  arrays: GeometryArrays,
  featureProperties?: object
) {
  switch (geojson.type) {
    case 'FeatureCollection':
      for (const feature of geojson.features) {
        iterateGeojson(feature, arrays);
      }
      break;
    case 'Feature':
      if (geojson.geometry.type === 'GeometryCollection') {
        iterateGeojson(geojson.geometry, arrays, geojson.properties);
      } else {
        addGeometry(geojson.geometry, arrays, geojson.properties);
      }
      break;
    case 'GeometryCollection':
      for (const geometry of geojson.geometries) {
        if (geometry.type === 'GeometryCollection') {
          iterateGeojson(geometry, arrays, featureProperties);
        } else {
          addGeometry(geometry, arrays, featureProperties);
        }
      }
      break;
    case 'Point':
    case 'MultiPoint':
    case 'LineString':
    case 'MultiLineString':
    case 'Polygon':
    case 'MultiPolygon':
      addGeometry(geojson, arrays, featureProperties);
      break;
    default:
      throw new Error(
        `Unknown GeoJSON type encountered ${(geojson as GeoJSON.GeoJSON).type}`
      );
  }
}

function addGeometry(
  geometry: GeoJSON.Geometry,
  arrays: GeometryArrays,
  featureProperties?: object
) {
  // Make sure there is always a name and description for the maps to avoid
  // field no exist errors
  featureProperties = {
    name: '',
    description: '',
    ...featureProperties,
  };
  switch (geometry.type) {
    case 'Point':
      arrays.points.push({
        type: 'Feature',
        properties: featureProperties,
        geometry,
      });
      break;
    case 'MultiPoint':
      arrays.multipoints.push({
        type: 'Feature',
        properties: featureProperties,
        geometry,
      });
      break;
    case 'LineString':
    case 'MultiLineString':
      arrays.polylines.push({
        type: 'Feature',
        properties: featureProperties,
        geometry,
      });
      break;
    case 'Polygon':
    case 'MultiPolygon':
      arrays.polygons.push({
        type: 'Feature',
        properties: featureProperties,
        geometry,
      });
      break;
    default:
      throw new Error(
        `Unknown GeoJSON geometry type encountered ${geometry.type}`
      );
  }
}
